import { useContext, useEffect, useMemo, useState } from "react";
import {useTheme} from "@mui/material";
import {useFieldArray, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {eParkingState } from "../../../../../search-repair-shop/state";
import {updateEparkingInfo} from "../../../../../search-repair-shop/rsSearch.actions";
import {SET_SNACKBAR} from "../../../../../common/utils";
import {GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import {Box, Button, Grid, Typography } from "@mui/material";
import {FormTextField } from "../../../../../common/form-fields-wrappers";
import { setValuesInForm } from "../../../../constants";
import TabWrapper from "../TabWrapper/TabWrapper";
import NewColumn from "./NewColumn";
import { EParkingForm } from "./types";

const EParking = () => {
    const {t} = useTranslation(['network', 'translation']);
    const {dispatchGlobalData} = useContext(GlobalContext);
    const theme = useTheme();

    const [eParkingInfo, setEParkingInfo] = useRecoilState(eParkingState);
    const [open, setOpen] = useState(false);
    const [messageError, setMessageError] = useState<string>('');

    const eParkingInfoForm = useForm<EParkingForm>({defaultValues: {
        id: eParkingInfo?.id ?? -1,
        chargingStationAddress: eParkingInfo?.chargingStationAddress ?? '',
        chargingStationStreetNumber: eParkingInfo?.chargingStationStreetNumber ?? '',
        evaluationOutcome: eParkingInfo?.evaluationOutcome ?? '',
        certificationProtocolIdentificationCode: eParkingInfo?.certificationProtocolIdentificationCode ?? '',
        evaluationNote: eParkingInfo?.evaluationNote ?? '',
        effectivePowerOnTheSite: eParkingInfo?.effectivePowerOnTheSite ?? '',
        requiredPowerOnTheSite: eParkingInfo?.requiredPowerOnTheSite ?? '',
        powerOnTheSiteNote: eParkingInfo?.powerOnTheSiteNote ?? '',
        chargingColumn: eParkingInfo?.chargingColumn ?? [],

    }});
    const chargingColumnArray = useFieldArray<EParkingForm>({ name: 'chargingColumn', control: eParkingInfoForm.control});

    const onSubmit = (data: any) => {
        const reqParameters =  {
            id: data.id,
            chargingStationAddress: data.chargingStationAddress,
            chargingStationStreetNumber: data.chargingStationStreetNumber,
            evaluationOutcome: data.evaluationOutcome,
            certificationProtocolIdentificationCode: data.certificationProtocolIdentificationCode,
            evaluationNote: data.evaluationNote,
            effectivePowerOnTheSite: data.effectivePowerOnTheSite,
            requiredPowerOnTheSite: data.requiredPowerOnTheSite,
            powerOnTheSiteNote: data.powerOnTheSiteNote,
            chargingColumn: data.chargingColumn
        };
        
        const typeError = handleErrorType(reqParameters.chargingColumn);
        
        if(['missingID','duplicateId'].includes(typeError)) {
            setMessageError(`rsDetail.eParking.message.error.${typeError}`);
            setOpen(false);
            return;
        }

        updateEparkingInfo(reqParameters)
            .then((res:any) => { setEParkingInfo(res) })
            .catch(() => { dispatchGlobalData(SET_SNACKBAR.ERROR) });
    }

    const handleOnDelete = (index?: number) => {
        chargingColumnArray.remove(index)
    }

    const handleAddColumn = () => {
        chargingColumnArray.append({
            chargingColumnId: "",
            chargingColumnType: "",
            numberOfChargingPoint: ""
        })
    }

    const handleErrorType = (arr: any): 'missingID' | 'duplicateId' | '' => {
        const remapArray = arr.map((obj: any) => obj.chargingColumnId);
        if(remapArray.includes('')) {
            return 'missingID'
        }
        if(remapArray.some((obj: any, index: any) => remapArray.indexOf(obj) !== index)) {
            return 'duplicateId';
        }
        return '';
    }

    useEffect(() => {
        if (!!eParkingInfo) {
            setValuesInForm(eParkingInfo, eParkingInfoForm);
        }
    },[eParkingInfo])

    const messageChargingColumn = useMemo(() => 
        chargingColumnArray.fields.length === 0 ?  "rsDetail.eParking.message.emptyState" : 'rsDetail.eParking.form.label.chargingColumnArea',
        [chargingColumnArray]
    );
    return(
        <TabWrapper infoForm={eParkingInfoForm} onSubmit={onSubmit} expanded={setOpen}>
            <>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled={!open}
                        control={eParkingInfoForm.control}
                        name="certificationProtocolIdentificationCode"
                        label={t("rsDetail.eParking.form.label.certificationProtocolIdentificationCode")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={eParkingInfoForm.control}
                        name="chargingStationIdentificationCode"
                        label={t("rsDetail.eParking.form.label.chargingStationIdentificationCode")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled
                        control={eParkingInfoForm.control}
                        name="chargingStationStatus"
                        label={t("rsDetail.eParking.form.label.chargingStationStatus")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled={!open}
                        control={eParkingInfoForm.control}
                        name="chargingStationAddress"
                        label={t("rsDetail.eParking.form.label.chargingStationAddress")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled={!open}
                        control={eParkingInfoForm.control}
                        name="chargingStationStreetNumber"
                        label={t("rsDetail.eParking.form.label.chargingStationStreetNumber")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled={!open}
                        control={eParkingInfoForm.control}
                        name="evaluationOutcome"
                        label={t("rsDetail.eParking.form.label.evaluationOutcome")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled={!open}
                        control={eParkingInfoForm.control}
                        name="evaluationNote"
                        label={t("rsDetail.eParking.form.label.evaluationNote")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled={!open}
                        control={eParkingInfoForm.control}
                        name="effectivePowerOnTheSite"
                        label={t("rsDetail.eParking.form.label.effectivePowerOnTheSite")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled={!open}
                        control={eParkingInfoForm.control}
                        name="requiredPowerOnTheSite"
                        label={t("rsDetail.eParking.form.label.requiredPowerOnTheSite")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormTextField
                        disabled={!open}
                        control={eParkingInfoForm.control}
                        name="powerOnTheSiteNote"
                        label={t("rsDetail.eParking.form.label.powerOnTheSiteNote")}
                    />
                </Grid>

                <Grid item container xs={12} marginTop={2}>
                    <Grid item container alignItems="center" xs={6}>
                        <Typography variant="bodySmall" color="grey">
                            {t(messageChargingColumn)}
                        </Typography>
                    </Grid>

                    {!!open &&
                    <Grid item container xs={6} justifyContent="flex-end" alignItems="flex-end">
                        <Box sx={{my: theme.spacing(2)}}>
                            <Button variant="text" color="accent" onClick={handleAddColumn}>
                                {t("rsDetail.eParking.button.addChargingStation")}
                            </Button>
                        </Box>
                    </Grid>
                }
                </Grid>

                <Grid item container xs={12} >
                    {chargingColumnArray.fields.map((field, index) => (
                        <Grid item container xs={12} marginBottom={2} columnSpacing={2} >
                            <NewColumn
                                field={field}
                                index={index}
                                eParkingInfoForm={eParkingInfoForm}
                                handleOnDelete={handleOnDelete}
                                open={open}
                                key={field.id}
                            />
                        </Grid>
                    ))}
                    
                    {messageError &&
                        <Grid item container xs={12} md={12}>
                            <Typography sx={{color: theme.palette.error.main}}> {t(messageError)} </Typography>
                        </Grid>
                    }
                </Grid>
            </>
        </TabWrapper>
    )
}

export default  EParking;
